import React, { useState, useEffect, useRef } from 'react';
import { 
    X, Plus, Trash2, UploadCloud, Save, FileText, 
    WandSparkles, Image as ImageIcon, ChevronDown, ChevronUp,
    Star, Camera, Award, Download, Eye, Layout, Users, Film, Settings,
    GripVertical, Move, ZoomIn, Check
} from 'lucide-react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

// --- SHARED UI COMPONENTS ---

const AppButton = ({ children, onClick, disabled, className = '', variant = 'primary', icon: Icon, size = 'md' }) => {
    const baseStyles = "flex items-center justify-center gap-2 font-bold rounded transition-all disabled:opacity-50 disabled:cursor-not-allowed";
    const variants = {
        primary: "text-white shadow-lg shadow-purple-900/20",
        secondary: "bg-zinc-800 text-zinc-300 hover:bg-zinc-700 hover:text-white border border-zinc-700",
        danger: "bg-red-500/10 text-red-500 hover:bg-red-500 hover:text-white"
    };
    const sizes = {
        sm: "px-3 py-1.5 text-xs",
        md: "px-4 py-2 text-sm"
    };

    return (
        <button 
            onClick={onClick} 
            disabled={disabled} 
            className={`${baseStyles} ${variants[variant] || variants.primary} ${sizes[size]} ${className}`}
        >
            {Icon && <Icon size={size === 'sm' ? 14 : 16} />}
            {children}
        </button>
    );
};

const AppInput = ({ value, onChange, placeholder, className = '' }) => (
    <input 
        value={value} 
        onChange={onChange} 
        placeholder={placeholder}
        className={`w-full bg-zinc-900 text-white border border-zinc-700 rounded px-3 py-2 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-all ${className}`}
    />
);

const AppTextarea = ({ value, onChange, placeholder, rows = 3, className = '' }) => (
    <textarea 
        value={value} 
        onChange={onChange} 
        placeholder={placeholder}
        rows={rows}
        className={`w-full bg-zinc-900 text-white border border-zinc-700 rounded px-3 py-2 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-all resize-none ${className}`}
    />
);

// --- FIXED IMAGE ADJUSTER (INTERACTIVE) ---
const ImageAdjuster = ({ settings, onUpdate, onClose, imageName }) => {
    const handleChange = (key, value) => {
        onUpdate({ ...settings, [key]: parseFloat(value) });
    };

    return (
        // CHANGE: 'justify-center' -> 'justify-end' and added 'pr-12' to dock it right
        <div className="fixed inset-0 z-[100] flex items-center justify-end pr-12 pointer-events-none">
            
            {/* Backdrop: Transparent but clickable to close */}
            <div className="absolute inset-0 bg-black/10 pointer-events-auto" onClick={onClose}></div>
            
            {/* Modal Content */}
            <div className="relative z-10 bg-zinc-950 border border-zinc-700 p-6 rounded-xl shadow-2xl w-80 animate-in fade-in slide-in-from-right-10 pointer-events-auto">
                <div className="flex justify-between items-center mb-4 pb-2 border-b border-zinc-800">
                    <span className="text-sm font-bold uppercase text-white flex items-center gap-2">
                        <Move size={14} className="text-purple-500"/> 
                        Adjust Image
                    </span>
                    <button onClick={onClose} className="hover:bg-zinc-800 p-1 rounded"><X size={16} className="text-zinc-500 hover:text-white"/></button>
                </div>
                
                {imageName && <p className="text-xs text-zinc-500 mb-4 uppercase tracking-wider font-bold text-center">{imageName}</p>}

                <div className="space-y-6">
                    <div>
                        <div className="flex justify-between text-xs text-zinc-400 mb-2 font-bold">
                            <span className="flex items-center gap-1">Pan Horizontal (X)</span>
                            <span>{settings.x}%</span>
                        </div>
                        <input 
                            type="range" min="0" max="100" 
                            value={settings.x} 
                            onChange={(e) => handleChange('x', e.target.value)}
                            className="w-full h-2 bg-zinc-800 rounded-lg appearance-none cursor-pointer accent-purple-500 hover:accent-purple-400"
                        />
                    </div>
                    <div>
                        <div className="flex justify-between text-xs text-zinc-400 mb-2 font-bold">
                            <span className="flex items-center gap-1">Pan Vertical (Y)</span>
                            <span>{settings.y}%</span>
                        </div>
                        <input 
                            type="range" min="0" max="100" 
                            value={settings.y} 
                            onChange={(e) => handleChange('y', e.target.value)}
                            className="w-full h-2 bg-zinc-800 rounded-lg appearance-none cursor-pointer accent-purple-500 hover:accent-purple-400"
                        />
                    </div>
                    <div>
                        <div className="flex justify-between text-xs text-zinc-400 mb-2 font-bold">
                            <span className="flex items-center gap-1"><ZoomIn size={12}/> Zoom / Scale</span>
                            <span>{settings.scale}x</span>
                        </div>
                        <input 
                            type="range" min="1" max="3" step="0.1"
                            value={settings.scale} 
                            onChange={(e) => handleChange('scale', e.target.value)}
                            className="w-full h-2 bg-zinc-800 rounded-lg appearance-none cursor-pointer accent-purple-500 hover:accent-purple-400"
                        />
                    </div>
                </div>

                <button onClick={onClose} className="mt-8 w-full bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold py-3 rounded uppercase tracking-wider flex items-center justify-center gap-2 transition-colors">
                    <Check size={14} /> Done
                </button>
            </div>
        </div>
    );
};

const LaurelWreath = ({ color = "currentColor", className }) => (
    <svg viewBox="0 0 120 120" fill={color} xmlns="http://www.w3.org/2000/svg" className={className}>
        {/* Left Branch */}
        <path d="M52 95C50 92 40 80 35 65C34 62 36 60 38 62C40 64 48 78 50 85C50.5 87 52 94 52 95Z" />
        <path d="M33 62C32 58 30 45 35 35C36 33 38 33 38 35C38 37 36 50 37 58C37.2 60 34 63 33 62Z" />
        <path d="M30 50C28 45 25 35 28 25C28.5 23 30.5 23 30 26C29 29 28 40 32 48C33 50 31 51 30 50Z" />
        <path d="M35 68C30 65 20 55 18 45C17.5 43 19.5 42 20 44C22 48 30 58 36 64C37 65 36 69 35 68Z" />
        <path d="M42 80C35 75 22 65 20 55C19.5 53 21.5 52 22 54C25 58 38 70 44 76C45 77 43 81 42 80Z" />
        <path d="M28 35C26 30 24 20 28 15C29 14 30 15 29.5 17C28 22 28 28 30 32C30.5 34 29 36 28 35Z" />

        {/* Right Branch (Mirrored) */}
        <path d="M68 95C70 92 80 80 85 65C86 62 84 60 82 62C80 64 72 78 70 85C69.5 87 68 94 68 95Z" />
        <path d="M87 62C88 58 90 45 85 35C84 33 82 33 82 35C82 37 84 50 83 58C82.8 60 86 63 87 62Z" />
        <path d="M90 50C92 45 95 35 92 25C91.5 23 89.5 23 90 26C91 29 92 40 88 48C87 50 89 51 90 50Z" />
        <path d="M85 68C90 65 100 55 102 45C102.5 43 100.5 42 100 44C98 48 90 58 84 64C83 65 84 69 85 68Z" />
        <path d="M78 80C85 75 98 65 100 55C100.5 53 98.5 52 98 54C95 58 82 70 76 76C75 77 77 81 78 80Z" />
        <path d="M92 35C94 30 96 20 92 15C91 14 90 15 90.5 17C92 22 92 28 90 32C89.5 34 91 36 92 35Z" />
    </svg>
);

// --- MOCKED AI SERVICE ---
const generateBioLLM = async (name, role, title) => {
    return new Promise((resolve) => {
        setTimeout(() => {
            resolve(`${name} is a visionary ${role} known for their work on "${title}". With a passion for storytelling that transcends genres, they bring a unique perspective to every project.`);
        }, 1500);
    });
};


// --- PROFESSIONAL PRESS KIT GENERATOR ---

// A4 Dimensions in mm
const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;
// Precise pixel dimensions for A4 @ 2x scale
const PAGE_WIDTH_PX = 794; 
const PAGE_HEIGHT_PX = 1123;

const TEMPLATES = {
    cinematic: {
        id: 'cinematic',
        name: 'Blockbuster (Dark)',
        bg: '#000000',
        text: '#ffffff',
        accent: '#EAB308', // Gold
        fontHead: 'font-oswald', 
        fontBody: 'font-montserrat',
    },
    cinematic_color: {
        id: 'cinematic_color',
        name: 'Action (Blue)',
        bg: '#09090b', // Very dark zinc
        text: '#ffffff',
        accent: '#3b82f6', // Blue
        fontHead: 'font-oswald', 
        fontBody: 'font-montserrat',
    },
    modern: {
        id: 'modern',
        name: 'Festival (Clean)',
        bg: '#ffffff',
        text: '#1a1a1a',
        accent: '#2563EB', // Blue
        fontHead: 'font-sans',
        fontBody: 'font-sans',
    },
    editorial: {
        id: 'editorial',
        name: 'Prestige (Indie)',
        bg: '#FDFBF7', // Warm Eggshell/Cream
        text: '#1C1917', // Soft Black
        accent: '#9F1239', // Rose/Burgundy
        fontHead: 'font-playfair', 
        fontBody: 'font-lora',
    }
};

const PAGE_TYPES = {
    COVER: 'cover',
    SYNOPSIS: 'synopsis',
    DIRECTOR_STATEMENT: 'director_statement',
    CAST_BIOS: 'cast_bios',
    CREW_BIOS: 'crew_bios',
    CREDITS_GRID: 'credits_grid',
    FACT_SHEET: 'fact_sheet',
    REVIEWS: 'reviews', 
    GALLERY: 'gallery', 
    PRODUCTION_COMPANY: 'production_company',
    CUSTOM: 'custom'
};

const ProfessionalPressKitGenerator = ({ 
    filmData, 
    uploadedImages = { posters: [], logos: [], headshots: {} }, 
    imageSettings = {}, 
    setUploadedImages,
    setNotification,
    user
}) => {
    const [activeTemplate, setActiveTemplate] = useState('cinematic');
    const [orientation, setOrientation] = useState('portrait'); 
    const [isGenerating, setIsGenerating] = useState(false);
    const [showPageManager, setShowPageManager] = useState(false);
    const [draggedItem, setDraggedItem] = useState(null);

    // Default page order
    const [pages, setPages] = useState([
        { id: 'cover', type: PAGE_TYPES.COVER, enabled: true, title: 'Cover', locked: true },
        { id: 'fact_sheet', type: PAGE_TYPES.FACT_SHEET, enabled: true, title: 'Film Details' },
        { id: 'synopsis', type: PAGE_TYPES.SYNOPSIS, enabled: true, title: 'Synopsis' },
        { id: 'director', type: PAGE_TYPES.DIRECTOR_STATEMENT, enabled: true, title: 'Director Statement' },
        { id: 'reviews', type: PAGE_TYPES.REVIEWS, enabled: true, title: 'Reviews & Laurels' },
        { id: 'cast_bios', type: PAGE_TYPES.CAST_BIOS, enabled: true, title: 'Cast Bios' },
        { id: 'crew_bios', type: PAGE_TYPES.CREW_BIOS, enabled: true, title: 'Crew Bios' },
        { id: 'gallery', type: PAGE_TYPES.GALLERY, enabled: false, title: 'Stills Gallery' },
        { id: 'credits', type: PAGE_TYPES.CREDITS_GRID, enabled: true, title: 'Full Credits' },
        { id: 'production', type: PAGE_TYPES.PRODUCTION_COMPANY, enabled: true, title: 'Back Cover' },
    ]);

    // --- INJECTIONS ---
    useEffect(() => {
        const link = document.createElement('link');
        link.href = 'https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,400;0,500;1,400&family=Montserrat:wght@300;400;500;600&family=Oswald:wght@400;600;700&family=Playfair+Display:ital,wght@0,400;0,700;1,400&display=swap';
        link.rel = 'stylesheet';
        document.head.appendChild(link);

        const scriptH2C = document.createElement('script');
        scriptH2C.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
        scriptH2C.async = true;
        document.body.appendChild(scriptH2C);

        const scriptPDF = document.createElement('script');
        scriptPDF.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
        scriptPDF.async = true;
        document.body.appendChild(scriptPDF);

        return () => {
            if(document.head.contains(link)) document.head.removeChild(link);
            if(document.body.contains(scriptH2C)) document.body.removeChild(scriptH2C);
            if(document.body.contains(scriptPDF)) document.body.removeChild(scriptPDF);
        };
    }, []);

    // --- PAGE MANAGEMENT ---
    const handleDragStart = (e, index) => { if (pages[index].locked) { e.preventDefault(); return; } setDraggedItem(index); e.dataTransfer.effectAllowed = 'move'; };
    const handleDragOver = (e, index) => { e.preventDefault(); if (index === 0 || draggedItem === null || draggedItem === index) return; const newPages = [...pages]; const draggedPage = newPages[draggedItem]; newPages.splice(draggedItem, 1); newPages.splice(index, 0, draggedPage); setPages(newPages); setDraggedItem(index); };
    const handleDragEnd = () => setDraggedItem(null);
    const togglePageEnabled = (pageId) => setPages(prev => prev.map(p => p.id === pageId && !p.locked ? { ...p, enabled: !p.enabled } : p));
    const movePage = (index, direction) => { if (pages[index].locked) return; const newIndex = direction === 'up' ? index - 1 : index + 1; if (newIndex <= 0 || newIndex >= pages.length) return; const newPages = [...pages]; [newPages[index], newPages[newIndex]] = [newPages[newIndex], newPages[index]]; setPages(newPages); };
    const addCustomPage = () => { const newPage = { id: `custom_${Date.now()}`, type: PAGE_TYPES.CUSTOM, enabled: true, title: 'Custom Page', content: 'Add content...' }; setPages(prev => [...prev, newPage]); };

    // --- STYLE HELPERS ---
    const getPageStyle = (template) => {
        const isLandscape = orientation === 'landscape';
        const width = isLandscape ? PAGE_HEIGHT_PX : PAGE_WIDTH_PX;
        const height = isLandscape ? PAGE_WIDTH_PX : PAGE_HEIGHT_PX;
        
        return {
            width: `${width}px`,
            height: `${height}px`,
            minWidth: `${width}px`,
            minHeight: `${height}px`,
            maxWidth: `${width}px`,
            maxHeight: `${height}px`,
            backgroundColor: template.bg,
            color: template.text,
            position: 'relative',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
            marginBottom: '40px'
        };
    };

    const getFontHead = (templateId) => {
        if (templateId.includes('cinematic')) return 'Oswald, sans-serif';
        if (templateId === 'editorial') return 'Playfair Display, serif';
        return 'sans-serif';
    };
    const getFontBody = (templateId) => {
        if (templateId.includes('cinematic')) return 'Montserrat, sans-serif';
        if (templateId === 'editorial') return 'Lora, serif';
        return 'sans-serif';
    };

    // --- RENDERERS ---

    const renderCover = () => {
        const template = TEMPLATES[activeTemplate];
        const poster = uploadedImages?.posters?.[0]?.url || filmData.posterUrl || filmData.poster_url;
        const title = filmData.title || "UNTITLED FILM";
        const settings = imageSettings['poster'] || { x: 50, y: 50, scale: 1 };
        
        return (
            <div className="press-page" style={getPageStyle(template)}>
                {poster ? (
                    <div className="w-full h-full flex items-center justify-center bg-black overflow-hidden">
                        <img 
                            src={poster} 
                            alt="Cover" 
                            crossOrigin="anonymous"
                            style={{
                                maxWidth: '100%',
                                maxHeight: '100%',
                                width: 'auto',
                                height: 'auto',
                                display: 'block',
                                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
                                transform: `scale(${settings.scale}) translate(${settings.x - 50}%, ${settings.y - 50}%)`,
                            }}
                        />
                    </div>
                ) : (
                    <div className="absolute inset-0 z-0 flex flex-col items-center justify-center" style={{ backgroundColor: template.bg }}>
                        <Film size={80} className="mb-4 opacity-20" style={{ color: template.text }} />
                        <h1 className="text-8xl font-bold uppercase tracking-tighter text-center px-10 leading-none" 
                            style={{ fontFamily: getFontHead(activeTemplate), color: template.text }}>
                            {title}
                        </h1>
                        <div className="w-24 h-2 mt-8" style={{ backgroundColor: template.accent }}></div>
                    </div>
                )}
            </div>
        );
    };

    const renderCastBios = () => {
        const template = TEMPLATES[activeTemplate];
        const cast = (filmData.cast || []).filter(m => m.bio);
        const isLandscape = orientation === 'landscape';
        if (cast.length === 0) return null;

        return cast.map((member, idx) => {
            const headshot = uploadedImages?.headshots?.[member.id]?.url || member.headshot_url;
            const containerStyle = {
                ...getPageStyle(template), 
                padding: 0, 
                flexDirection: isLandscape ? 'row' : 'column' 
            };
            const settings = imageSettings[member.id] || { x: 50, y: 50, scale: 1 };

            return (
                <div key={member.id || idx} className="press-page" style={containerStyle}>
                    <div className={`relative overflow-hidden bg-zinc-900 flex-shrink-0 ${isLandscape ? 'w-1/2 h-full' : 'w-full h-[45%]'}`}>
                         {headshot ? (
                            <div className="w-full h-full flex items-center justify-center overflow-hidden">
                                <img 
                                    src={headshot}
                                    alt={member.name}
                                    crossOrigin="anonymous"
                                    style={{
                                        maxWidth: '100%',
                                        maxHeight: '100%',
                                        width: 'auto',
                                        height: 'auto',
                                        display: 'block',
                                        transform: `scale(${settings.scale}) translate(${settings.x - 50}%, ${settings.y - 50}%)`,
                                    }}
                                />
                            </div>
                         ) : (
                            <div className="w-full h-full flex items-center justify-center bg-zinc-800"><Users size={64} className="text-zinc-700" /></div>
                         )}
                         <div className="absolute inset-0" style={{ backgroundColor: 'rgba(0,0,0,0.2)' }}></div>
                    </div>
                    
                    <div className={`${isLandscape ? 'w-1/2 h-full p-16' : 'w-full h-[55%] p-12'} flex flex-col justify-center`}>
                        <h3 className="text-5xl font-bold uppercase tracking-tight leading-none mb-2" style={{ fontFamily: getFontHead(activeTemplate) }}>{member.name}</h3>
                        <p className="text-xl uppercase tracking-[0.2em] mb-8 opacity-70" style={{ color: template.accent, fontFamily: getFontHead(activeTemplate) }}>as "{member.role}"</p>
                        <div className="w-16 h-1 mb-8 opacity-50" style={{ backgroundColor: template.text }}></div>
                        <p className="text-lg leading-relaxed font-light opacity-80 text-justify" style={{ fontFamily: getFontBody(activeTemplate) }}>{member.bio}</p>
                    </div>
                </div>
            );
        });
    };

    const renderCrewBios = () => {
        const template = TEMPLATES[activeTemplate];
        const crew = (filmData.crew || []).filter(m => m.bio);
        const isLandscape = orientation === 'landscape';
        if (crew.length === 0) return null;

        return crew.map((member, idx) => {
            const headshot = uploadedImages?.headshots?.[member.id]?.url || member.headshot_url;
            const containerStyle = {
                ...getPageStyle(template), 
                padding: 0, 
                flexDirection: isLandscape ? 'row-reverse' : 'column'
            };
            const settings = imageSettings[member.id] || { x: 50, y: 50, scale: 1 };

            return (
                <div key={member.id || idx} className="press-page" style={containerStyle}>
                    <div className={`relative overflow-hidden bg-zinc-900 flex-shrink-0 ${isLandscape ? 'w-1/2 h-full' : 'w-full h-[45%]'}`}>
                         {headshot ? (
                             <div className="w-full h-full flex items-center justify-center overflow-hidden">
                                <img 
                                    src={headshot}
                                    alt={member.name}
                                    crossOrigin="anonymous"
                                    style={{
                                        maxWidth: '100%',
                                        maxHeight: '100%',
                                        width: 'auto',
                                        height: 'auto',
                                        display: 'block',
                                        transform: `scale(${settings.scale}) translate(${settings.x - 50}%, ${settings.y - 50}%)`,
                                    }}
                                />
                             </div>
                         ) : (
                            <div className="w-full h-full flex items-center justify-center bg-zinc-800"><Film size={64} className="text-zinc-700" /></div>
                         )}
                         <div className="absolute inset-0" style={{ backgroundColor: 'rgba(0,0,0,0.2)' }}></div>
                    </div>
                    
                    <div className={`${isLandscape ? 'w-1/2 h-full p-16 items-end text-right' : 'w-full h-[55%] p-12 items-start text-left'} flex flex-col justify-center`}>
                        <h3 className="text-5xl font-bold uppercase tracking-tight leading-none mb-2" style={{ fontFamily: getFontHead(activeTemplate) }}>{member.name}</h3>
                        <p className="text-xl uppercase tracking-[0.2em] mb-8 opacity-70" style={{ color: template.accent, fontFamily: getFontHead(activeTemplate) }}>{member.role}</p>
                        <div className="w-16 h-1 mb-8 opacity-50" style={{ backgroundColor: template.text }}></div>
                        <p className="text-lg leading-relaxed font-light opacity-80 text-justify" style={{ fontFamily: getFontBody(activeTemplate) }}>{member.bio}</p>
                    </div>
                </div>
            );
        });
    };

    // ... Standard renderers (Synopsis, Director, etc.) ...
    const renderSynopsis = () => {
        const template = TEMPLATES[activeTemplate];
        if (!filmData.synopsis) return null;
        return (
            <div className="press-page" style={{ ...getPageStyle(template), padding: '80px' }}>
                <div className="mb-10 border-l-4 pl-8" style={{ borderColor: template.accent }}>
                    <h2 className="text-6xl font-bold uppercase tracking-tight mb-2" style={{ fontFamily: getFontHead(activeTemplate) }}>Synopsis</h2>
                </div>
                <div className="flex-1 overflow-hidden">
                    <p className="text-2xl leading-relaxed font-light text-justify opacity-90 whitespace-pre-wrap" style={{ fontFamily: getFontBody(activeTemplate) }}>{filmData.synopsis}</p>
                </div>
            </div>
        );
    };

    const renderDirectorStatement = () => {
        const template = TEMPLATES[activeTemplate];
        if (!filmData.director_statement) return null;
        return (
             <div className="press-page" style={{ ...getPageStyle(template), padding: '80px' }}>
                <div className="mb-12">
                    <h2 className="text-6xl font-bold uppercase tracking-tight leading-none" style={{ fontFamily: getFontHead(activeTemplate), color: template.accent }}>Director's</h2>
                    <h2 className="text-6xl font-bold uppercase tracking-tight leading-none" style={{ fontFamily: getFontHead(activeTemplate) }}>Statement</h2>
                </div>
                <div className="flex-1">
                     <p className="text-2xl leading-relaxed font-light text-justify opacity-90 whitespace-pre-wrap italic" style={{ fontFamily: getFontBody(activeTemplate) }}>"{filmData.director_statement}"</p>
                     <p className="mt-10 font-bold uppercase tracking-widest text-right opacity-70" style={{ fontFamily: getFontHead(activeTemplate) }}>— {filmData.director || 'The Director'}</p>
                </div>
            </div>
        );
    };

    const renderReviews = () => {
        const template = TEMPLATES[activeTemplate];
        const reviews = filmData.reviews || [];
        const laurels = filmData.laurels || [];
        
        if (reviews.length === 0 && laurels.length === 0) return null;

        return (
            <div className="press-page" style={{ ...getPageStyle(template), padding: '80px', justifyContent: 'center' }}>
                {/* UPDATED LAUREL SECTION */}
                {laurels.length > 0 && (
                    <div className="flex justify-center gap-4 mb-16 opacity-90 flex-wrap">
                        {laurels.map((laurel, i) => (
                            <div key={laurel.id || i} className="relative flex items-center justify-center w-40 h-32 transform hover:scale-105 transition-transform">
                                {/* Background SVG */}
                                <LaurelWreath 
                                    color={template.accent} 
                                    className="absolute inset-0 w-full h-full" 
                                />
                                
                                {/* Text Content positioned inside wreath */}
                                <div className="relative z-10 flex flex-col items-center text-center px-4 pt-2">
                                    <span 
                                        className="text-[9px] uppercase tracking-widest font-bold leading-tight mb-1" 
                                        style={{ fontFamily: getFontHead(activeTemplate) }}
                                    >
                                        {laurel.award_name}
                                    </span>
                                    
                                    <span 
                                        className="text-[8px] uppercase leading-tight px-1"
                                        style={{ fontFamily: getFontBody(activeTemplate) }}
                                    >
                                        {laurel.festival}
                                    </span>
                                    
                                    {laurel.year && (
                                        <span className="text-[8px] font-bold mt-1 opacity-60">
                                            {laurel.year}
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Reviews Section (Unchanged) */}
                <div className="flex flex-col gap-12 items-center">
                    {reviews.map((review, idx) => (
                        <div key={idx} className="text-center max-w-4xl">
                            <p className="text-3xl italic font-light leading-tight mb-4" style={{ fontFamily: getFontBody(activeTemplate) }}>"{review.quote}"</p>
                            {(review.source || review.author) && (
                                <div className="flex items-center justify-center gap-3">
                                    <div className="h-px w-8 bg-current opacity-50"></div>
                                    <span className="text-lg font-bold uppercase tracking-widest" style={{ fontFamily: getFontHead(activeTemplate), color: template.accent }}>{review.source}</span>
                                    {review.author && <span className="opacity-50 text-sm uppercase tracking-wide">— {review.author}</span>}
                                    <div className="h-px w-8 bg-current opacity-50"></div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const renderGallery = () => {
        const template = TEMPLATES[activeTemplate];
        const isLandscape = orientation === 'landscape';
        const gallery = filmData.gallery || [];
        if (gallery.length === 0) return null;
        return (
            <div className="press-page" style={{ ...getPageStyle(template), padding: '60px' }}>
                <div className="mb-8">
                    <h2 className="text-4xl font-bold uppercase tracking-tight" style={{ fontFamily: getFontHead(activeTemplate) }}>Production Stills</h2>
                </div>
                <div className={`grid ${isLandscape ? 'grid-cols-4' : 'grid-cols-2'} gap-4 flex-1 content-start`}>
                    {gallery.map((item, idx) => (
                        <div key={item.id || idx} className="aspect-video bg-zinc-900 flex items-center justify-center rounded overflow-hidden relative group">
                             <div 
                                className="w-full h-full"
                                style={{
                                    backgroundImage: `url(${item.url})`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                }}
                             />
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const renderCreditsGrid = () => {
        const template = TEMPLATES[activeTemplate];
        const credits = filmData.full_credits || [];
        if (credits.length === 0) return null;
        const creditsPerPage = 33; 
        const totalPages = Math.ceil(credits.length / creditsPerPage);
        const pagesRender = [];
        for (let pageNum = 0; pageNum < totalPages; pageNum++) {
            const pageCredits = credits.slice(pageNum * creditsPerPage, (pageNum + 1) * creditsPerPage);
            const colSize = Math.ceil(pageCredits.length / 3);
            const col1 = pageCredits.slice(0, colSize);
            const col2 = pageCredits.slice(colSize, colSize * 2);
            const col3 = pageCredits.slice(colSize * 2);
            pagesRender.push(
                <div key={`credits-${pageNum}`} className="press-page" style={{ ...getPageStyle(template), padding: '40px 80px' }}>
                    {pageNum === 0 && (
                        <div className="mb-8 text-center">
                             <h2 className="text-5xl font-bold uppercase tracking-[0.2em]" style={{ fontFamily: getFontHead(activeTemplate) }}>Full Credits</h2>
                            <div className="h-1 w-24 mx-auto mt-4" style={{ backgroundColor: `${template.text}40` }}></div>
                        </div>
                    )}
                    <div className="flex-1 flex gap-10 items-start content-start">
                        {[col1, col2, col3].map((col, cIdx) => (
                            <div key={cIdx} className="flex-1 flex flex-col gap-6 items-center"> 
                                {col.map((credit, idx) => (
                                    <div key={idx} className="text-center w-full">
                                        <p className="text-[10px] uppercase tracking-widest font-bold opacity-50 mb-1 leading-none" style={{ fontFamily: getFontBody(activeTemplate) }}>{credit.role}</p>
                                        <p className="text-sm font-bold uppercase tracking-wide leading-tight" style={{ fontFamily: getFontHead(activeTemplate) }}>{credit.name}</p>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            );
        }
        return pagesRender;
    };

    const renderFactSheet = () => {
        const template = TEMPLATES[activeTemplate];
        const isLandscape = orientation === 'landscape';
        const facts = [
            { label: 'Title', value: filmData.title },
            { label: 'Runtime', value: filmData.runtime },
            { label: 'Release', value: filmData.releaseWindow },
            { label: 'Genre', value: filmData.genre },
            { label: 'Production Co', value: filmData.production_company },
        ].filter(f => f.value);
        return (
            <div className="press-page" style={{ ...getPageStyle(template), padding: '80px' }}>
                <div className="mb-12 border-b pb-8" style={{ borderColor: `${template.text}40` }}>
                    <h2 className="text-5xl font-bold uppercase tracking-tight" style={{ fontFamily: getFontHead(activeTemplate) }}>Film Details</h2>
                </div>
                <div className={`flex ${isLandscape ? 'flex-row gap-16' : 'flex-col gap-10'}`}>
                    {filmData.logline && (
                        <div className={`${isLandscape ? 'flex-[1.5]' : 'mb-6'}`}>
                            <span className="text-sm uppercase tracking-[0.2em] opacity-50 mb-2 block" style={{ fontFamily: getFontHead(activeTemplate) }}>Logline</span>
                            <p className="text-2xl font-light leading-relaxed" style={{ fontFamily: getFontBody(activeTemplate) }}>"{filmData.logline}"</p>
                        </div>
                    )}
                    <div className={`flex flex-col ${isLandscape ? 'flex-1 gap-8' : 'gap-10'}`}>
                        {facts.map((fact, idx) => (
                            <div key={idx} className="flex flex-col">
                                <span className="text-sm uppercase tracking-[0.2em] opacity-50 mb-1" style={{ fontFamily: getFontHead(activeTemplate) }}>{fact.label}</span>
                                <span className="text-4xl font-bold uppercase" style={{ fontFamily: getFontHead(activeTemplate) }}>{fact.value}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    const renderProductionCompany = () => {
        const template = TEMPLATES[activeTemplate];
        const company = filmData.production_company;
        const logos = uploadedImages?.logos || [];
        if (!company && logos.length === 0) return null;
        return (
            <div className="press-page" style={{ ...getPageStyle(template), padding: '80px', justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}>
                <div className="flex-1 flex flex-col justify-center items-center">
                    {company && (
                        <div className="mb-16">
                            <p className="text-sm uppercase tracking-[0.4em] opacity-50 mb-6" style={{ fontFamily: getFontHead(activeTemplate) }}>A Production By</p>
                            <h2 className="text-6xl font-bold uppercase tracking-widest" style={{ fontFamily: getFontHead(activeTemplate), color: template.accent }}>{company}</h2>
                        </div>
                    )}
                </div>
                <div className="w-full p-10 bg-white rounded-xl flex flex-wrap justify-center items-center gap-16 mb-16">
                    {logos.length > 0 ? logos.map((logo, idx) => (
                        <img key={logo.id || idx} src={logo.url} alt="Logo" className="h-20 object-contain" style={{ filter: 'grayscale(100%)' }} crossOrigin="anonymous" />
                    )) : (
                        <div className="text-black/30 text-sm font-bold uppercase tracking-widest">Add Company Logos</div>
                    )}
                </div>
                <div className="mt-auto opacity-50 text-xs tracking-[0.2em]">CONTACT: {user?.email || 'PRESS@PRODUCTION.COM'}</div>
            </div>
        );
    };

    const renderCustomPage = (page) => {
        const template = TEMPLATES[activeTemplate];
        return (
            <div className="press-page" style={{ ...getPageStyle(template), padding: '80px' }}>
                <div className="mb-8">
                     <h2 className="text-4xl font-bold uppercase tracking-wider" style={{ fontFamily: getFontHead(activeTemplate) }}>{page.title}</h2>
                </div>
                <div className="flex-1">
                     <p className="text-lg leading-relaxed whitespace-pre-wrap opacity-90" style={{ fontFamily: getFontBody(activeTemplate) }}>{page.content}</p>
                </div>
            </div>
        );
    };

    const renderPageByType = (page) => {
        switch(page.type) {
            case PAGE_TYPES.COVER: return renderCover();
            case PAGE_TYPES.SYNOPSIS: return renderSynopsis();
            case PAGE_TYPES.DIRECTOR_STATEMENT: return renderDirectorStatement();
            case PAGE_TYPES.REVIEWS: return renderReviews(); 
            case PAGE_TYPES.GALLERY: return renderGallery(); 
            case PAGE_TYPES.CAST_BIOS: return renderCastBios();
            case PAGE_TYPES.CREW_BIOS: return renderCrewBios();
            case PAGE_TYPES.CREDITS_GRID: return renderCreditsGrid();
            case PAGE_TYPES.FACT_SHEET: return renderFactSheet();
            case PAGE_TYPES.PRODUCTION_COMPANY: return renderProductionCompany();
            case PAGE_TYPES.CUSTOM: return renderCustomPage(page);
            default: return null;
        }
    };

    const generatePDF = async () => {
        setIsGenerating(true);
        setNotification({ type: 'info', message: 'Preparing High-Res PDF...' });
        
        try {
            if (!window.html2canvas || !window.jspdf) throw new Error('PDF Libraries loading...');
            const { jsPDF } = window.jspdf;
            
            const pdf = new jsPDF({
                orientation: orientation === 'landscape' ? 'l' : 'p',
                unit: 'mm',
                format: 'a4'
            });
            
            const pdfWidth = orientation === 'landscape' ? A4_HEIGHT_MM : A4_WIDTH_MM;
            const pdfHeight = orientation === 'landscape' ? A4_WIDTH_MM : A4_HEIGHT_MM;
            
            const pageElements = document.querySelectorAll('.press-page');
            
            for (let i = 0; i < pageElements.length; i++) {
                const element = pageElements[i];
                
                // 1. Capture
                const canvas = await window.html2canvas(element, {
                    scale: 2, // High Quality
                    useCORS: true, // Critical for images
                    allowTaint: true,
                    backgroundColor: TEMPLATES[activeTemplate].bg,
                    logging: false,
                    onclone: (clonedDoc) => {
                        // Font Injection
                        const link = document.createElement('link');
                        link.href = 'https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,400;0,500;1,400&family=Montserrat:wght@300;400;500;600&family=Oswald:wght@400;600;700&family=Playfair+Display:ital,wght@0,400;0,700;1,400&display=swap';
                        link.rel = 'stylesheet';
                        clonedDoc.head.appendChild(link);
                    }
                });
                
                const imgData = canvas.toDataURL('image/jpeg', 0.95);
                
                if (i > 0) pdf.addPage();
                pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);
            }
            
            pdf.save(`${filmData.title || 'Press_Kit'}.pdf`);
            setNotification({ type: 'success', message: 'PDF Exported Successfully!' });
        } catch (error) {
            console.error(error);
            setNotification({ type: 'error', message: 'Export failed. Please verify image permissions.' });
        } finally {
            setIsGenerating(false);
        }
    };

    return (
        <div className="h-full flex flex-col bg-zinc-950 text-white font-sans">
            {/* Header Controls */}
            <div className="flex items-center justify-between p-4 border-b border-zinc-800 bg-zinc-900 sticky top-0 z-50">
                <div className="flex items-center gap-4">
                    <div className="flex gap-1 bg-zinc-800 rounded p-1">
                        <button onClick={() => setOrientation('portrait')} className={`px-3 py-1.5 text-xs font-bold rounded transition-all ${orientation==='portrait' ?'bg-purple-600 text-white' :'text-zinc-400 hover:bg-zinc-700'}`} title="Portrait"><Layout size={14} /></button>
                        <button onClick={() => setOrientation('landscape')} className={`px-3 py-1.5 text-xs font-bold rounded transition-all ${orientation==='landscape' ?'bg-purple-600 text-white' :'text-zinc-400 hover:bg-zinc-700'}`} title="Landscape"><Layout size={14} className="rotate-90" /></button>
                    </div>
                    <div className="flex gap-2">
                        {Object.values(TEMPLATES).map(t => (
                            <button 
                                key={t.id}
                                onClick={() => setActiveTemplate(t.id)} 
                                className={`px-3 py-1.5 text-xs font-bold rounded border transition-all ${activeTemplate===t.id ? 'bg-zinc-800 border-purple-500 text-purple-400' :'border-zinc-700 text-zinc-400'}`}
                            >
                                {t.name}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="flex gap-2">
                    <button onClick={() => setShowPageManager(!showPageManager)} className={`px-3 py-2 rounded flex items-center gap-2 text-xs font-bold uppercase tracking-wide transition-all ${showPageManager ? 'bg-purple-600' : 'bg-zinc-800'}`}><Settings size={14}/> Layout</button>
                    <button onClick={generatePDF} disabled={isGenerating} className="px-4 py-2 bg-white text-black rounded flex items-center gap-2 text-xs font-bold uppercase tracking-wide hover:bg-zinc-200 transition-all disabled:opacity-50">{isGenerating ? 'Processing...' : <><Download size={14}/> Export PDF</>}</button>
                </div>
            </div>

            <div className="flex flex-1 overflow-hidden relative">
                {/* Page Manager (Left Sidebar overlay) */}
                {showPageManager && (
                    <div className="absolute left-0 top-0 bottom-0 w-64 bg-zinc-900 border-r border-zinc-800 z-40 p-4 overflow-y-auto animate-in slide-in-from-left-10">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-bold text-xs uppercase tracking-wider text-zinc-500">Page Order</h3>
                            <button onClick={addCustomPage} className="text-xs text-purple-400 hover:text-purple-300 flex items-center gap-1"><Plus size={12} /> Add</button>
                        </div>
                        <div className="space-y-2">
                            {pages.map((page, index) => (
                                <div key={page.id} draggable={!page.locked} onDragStart={(e) => handleDragStart(e, index)} onDragOver={(e) => handleDragOver(e, index)} onDragEnd={handleDragEnd} className={`group bg-zinc-800 p-3 rounded border transition-all ${page.locked ? 'border-purple-500/30 cursor-default' : 'border-zinc-700 hover:border-zinc-600 cursor-move'} ${draggedItem === index ? 'opacity-50' : ''} ${!page.enabled ? 'opacity-40' : ''}`}>
                                    <div className="flex items-center gap-2">
                                        {page.locked ? <div className="w-4 flex justify-center"><div className="w-1.5 h-1.5 rounded-full bg-purple-500" /></div> : <GripVertical size={14} className="text-zinc-600 group-hover:text-zinc-400" />}
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2">
                                                {!page.locked && <input type="checkbox" checked={page.enabled} onChange={() => togglePageEnabled(page.id)} className="rounded accent-purple-500" />}
                                                <span className={`text-xs font-bold ${page.locked ? 'text-purple-300' : 'text-zinc-300'}`}>{page.title}</span>
                                            </div>
                                        </div>
                                        {!page.locked && (
                                            <div className="flex gap-1">
                                                <button onClick={() => movePage(index, 'up')} disabled={index <= 1} className="hover:text-white text-zinc-600"><ChevronUp size={12} /></button>
                                                <button onClick={() => movePage(index, 'down')} disabled={index === pages.length - 1} className="hover:text-white text-zinc-600"><ChevronDown size={12} /></button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Main Preview Area */}
                <div className="flex-1 overflow-y-auto p-8 bg-zinc-950 flex flex-col items-center gap-8 shadow-inner">
                    {pages.filter(page => page.enabled).map((page) => {
                        const renderedContent = renderPageByType(page);
                        if (Array.isArray(renderedContent)) {
                            return renderedContent.map((pageElement, idx) => <React.Fragment key={`${page.id}-${idx}`}>{pageElement}</React.Fragment>);
                        }
                        return renderedContent ? <React.Fragment key={page.id}>{renderedContent}</React.Fragment> : null;
                    })}
                </div>
            </div>
            
            {/* REMOVED THE INCORRECT IMAGE ADJUSTER BLOCK FROM HERE */}
            
        </div>
    );
};

// --- MAIN PRESS KIT BUILDER MODAL COMPONENT ---

const PressKitBuilderModal = ({ 
    isOpen, 
    onClose, 
    filmData = { title: "Untitled Film", cast: [], crew: [] }, 
    db, 
    storage, 
    user,
    setNotification = () => {}, 
    saveFilmData 
}) => {
    // --- STATE MANAGEMENT ---
    
    // Main data state
    const [pressKitData, setPressKitData] = useState({
        logline: '',
        synopsis: '',
        director_statement: '',
        runtime: '',
        full_credits: [],
        posterUrl: '',
        production_company: '',
        reviews: [], 
        laurels: [], 
        gallery: [],
        imageSettings: {} // New state for storing x/y/scale of images
    });
    
    // Local image preview state
    const [uploadedImages, setUploadedImages] = useState({
        posters: [],
        logos: [],
        headshots: {},
        gallery: [] 
    });

    const [isSaving, setIsSaving] = useState(false);
    const [isGeneratingBio, setIsGeneratingBio] = useState(false);
    const [openSections, setOpenSections] = useState({
        poster: true,
        details: true,
        credits: false,
        reviews: false,
        gallery: false
    });
    
    const [adjustingImage, setAdjustingImage] = useState(null); // Tracks which image is being adjusted
    const csvInputRef = useRef(null);

    // --- INITIALIZATION ---
    useEffect(() => {
        if (!isOpen || !filmData) return;

        const initialData = filmData.press_kit || {};
        const posterUrl = initialData.posterUrl || filmData.posterUrl || filmData.poster_a || filmData.poster_url || filmData.posterImageUrl || '';
        
        setPressKitData({
            logline: filmData.logline || initialData.logline || '',
            synopsis: initialData.synopsis || '',
            director_statement: initialData.director_statement || '',
            runtime: initialData.runtime || '',
            full_credits: Array.isArray(initialData.full_credits) ? initialData.full_credits : [],
            posterUrl: posterUrl,
            production_company: initialData.production_company || filmData.production_company || '',
            reviews: initialData.reviews || [],
            laurels: initialData.laurels || [],
            gallery: initialData.gallery || [],
            imageSettings: initialData.imageSettings || {} // Load saved settings
        });

        setUploadedImages({
            posters: posterUrl ? [{ url: posterUrl, id: 'default_poster' }] : [],
            logos: initialData.logos || [], 
            headshots: initialData.headshots || {},
            gallery: initialData.gallery ? initialData.gallery.map(g => ({ ...g, id: g.id || Date.now() })) : []
        });
    }, [isOpen, filmData]);

    // --- IMAGE ADJUSTMENT HANDLER ---
    const handleImageSettingsUpdate = (imageId, newSettings) => {
        setPressKitData(prev => ({
            ...prev,
            imageSettings: {
                ...prev.imageSettings,
                [imageId]: newSettings
            }
        }));
    };

// --- REVIEWS & LAURELS LOGIC ---
    const handleAddReview = () => {
        setPressKitData(prev => ({
            ...prev,
            reviews: [...prev.reviews, { id: Date.now(), source: '', quote: '', author: '' }]
        }));
    };

    const updateReview = (index, field, value) => {
        const newReviews = [...pressKitData.reviews];
        newReviews[index][field] = value;
        setPressKitData(prev => ({ ...prev, reviews: newReviews }));
    };

    const removeReview = (index) => {
        setPressKitData(prev => ({
            ...prev,
            reviews: prev.reviews.filter((_, i) => i !== index)
        }));
    };

    const handleAddLaurel = () => {
        setPressKitData(prev => ({
            ...prev,
            laurels: [...prev.laurels, { id: Date.now(), festival: '', award_name: 'Official Selection', year: new Date().getFullYear() }]
        }));
    };

    const updateLaurel = (index, field, value) => {
        const newLaurels = [...pressKitData.laurels];
        newLaurels[index][field] = value;
        setPressKitData(prev => ({ ...prev, laurels: newLaurels }));
    };

    const removeLaurel = (index) => {
        setPressKitData(prev => ({
            ...prev,
            laurels: prev.laurels.filter((_, i) => i !== index)
        }));
    };

  
    const handleGalleryUpload = async (e) => {
        if (!e.target.files || e.target.files.length === 0) return;
        const files = Array.from(e.target.files);
        const newGalleryItems = [];
        files.forEach(file => { newGalleryItems.push({ url: URL.createObjectURL(file), file, id: `temp_${Date.now()}_${Math.random()}` }); });
        setUploadedImages(prev => ({ ...prev, gallery: [...prev.gallery, ...newGalleryItems] }));
        setNotification({ type: 'info', message: `Uploading ${files.length} photos...` });
        const uploadPromises = newGalleryItems.map(async (item) => {
            try {
                const storageRef = ref(storage, `press_kits/${filmData.id}/gallery/${Date.now()}_${item.file.name}`);
                const snap = await uploadBytes(storageRef, item.file);
                const url = await getDownloadURL(snap.ref);
                return { ...item, url, file: null };
            } catch (error) { console.error("Gallery upload error:", error); return null; }
        });
        const uploadedItems = (await Promise.all(uploadPromises)).filter(Boolean);
        setUploadedImages(prev => ({ ...prev, gallery: prev.gallery.map(g => { const uploaded = uploadedItems.find(u => u.id === g.id); return uploaded || g; }) }));
        setPressKitData(prev => ({ ...prev, gallery: [...prev.gallery, ...uploadedItems.map(u => ({ id: u.id, url: u.url }))] }));
        setNotification({ type: 'success', message: 'Gallery updated!' });
    };
    const removeGalleryImage = (id) => { setUploadedImages(prev => ({ ...prev, gallery: prev.gallery.filter(g => g.id !== id) })); setPressKitData(prev => ({ ...prev, gallery: prev.gallery.filter(g => g.id !== id) })); };

    const handleCsvImport = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (event) => {
            const text = event.target.result;
            const parseCSV = (str) => { const arr = []; let quote = false; for (let row = 0, col = 0, c = 0; c < str.length; c++) { let cc = str[c], nc = str[c+1]; arr[row] = arr[row] || []; arr[row][col] = arr[row][col] || ''; if (cc === '"' && quote && nc === '"') { arr[row][col] += cc; ++c; } else if (cc === '"') { quote = !quote; } else if (cc === ',' && !quote) { ++col; } else if (cc === '\r' && nc === '\n' && !quote) { ++row; col = 0; ++c; } else if (cc === '\n' && !quote) { ++row; col = 0; } else if (cc === '\r' && !quote) { ++row; col = 0; } else { arr[row][col] += cc; } } return arr; };
            const rows = parseCSV(text);
            const dataRows = rows.slice(1).filter(row => row.length >= 2 && row[0]);
            const newCredits = dataRows.map(row => ({ id: crypto.randomUUID(), name: (row[0] || '').trim(), role: (row[1] || '').trim(), department: (row[2] || '').trim() === 'Cast' ? 'Cast' : 'Crew', bio: row.slice(3).filter(p => p && p.trim()).join(' ').trim(), is_featured: false, type: (row[2] || '').trim() === 'Cast' ? 'cast' : 'crew', headshot_url: '' })).filter(c => c.name && c.role);
            setPressKitData(prev => ({ ...prev, full_credits: [...prev.full_credits, ...newCredits] }));
            setNotification({ type: 'success', message: `Imported ${newCredits.length} credits successfully.` });
        };
        reader.readAsText(file);
        if (csvInputRef.current) csvInputRef.current.value = '';
    };

    const handleAddCredit = () => { setPressKitData(prev => ({ ...prev, full_credits: [...prev.full_credits, { id: crypto.randomUUID(), name: '', role: '', bio: '', is_featured: false, type: 'crew', headshot_url: '' }] })); };
    const handleCreditChange = (index, field, value) => { setPressKitData(prev => { const newCredits = [...prev.full_credits]; newCredits[index] = { ...newCredits[index], [field]: value }; return { ...prev, full_credits: newCredits }; }); };
    const handleDeleteCredit = (index) => { setPressKitData(prev => ({ ...prev, full_credits: prev.full_credits.filter((_, i) => i !== index) })); };
    const handleToggleFeature = (index) => { setPressKitData(prev => { const newCredits = [...prev.full_credits]; newCredits[index] = { ...newCredits[index], is_featured: !newCredits[index].is_featured }; return { ...prev, full_credits: newCredits }; }); };

    const handleGenerateBio = async (index) => {
        const credit = pressKitData.full_credits[index];
        if (!credit.name || !credit.role) { setNotification({ type: 'error', message: 'Name and role required' }); return; }
        setIsGeneratingBio(true);
        try {
            const bio = await generateBioLLM(credit.name, credit.role, filmData.title);
            handleCreditChange(index, 'bio', bio);
            setNotification({ type: 'success', message: 'Bio generated!' });
        } catch (error) { console.error('Bio error:', error); setNotification({ type: 'error', message: 'Bio generation failed' }); } finally { setIsGeneratingBio(false); }
    };

    const handlePosterUpload = async (e) => {
        if (!e.target.files[0]) return;
        const file = e.target.files[0];
        const localUrl = URL.createObjectURL(file);
        setPressKitData(prev => ({ ...prev, posterUrl: localUrl }));
        setUploadedImages(prev => ({ ...prev, posters: [{ url: localUrl, file, id: Date.now() }] }));
        try {
            const storageRef = ref(storage, `press_kits/${filmData.id}/posters/${Date.now()}_${file.name}`);
            const snap = await uploadBytes(storageRef, file);
            const url = await getDownloadURL(snap.ref);
            setPressKitData(prev => ({ ...prev, posterUrl: url }));
            setUploadedImages(prev => ({ ...prev, posters: [{ url, file, id: Date.now() }] }));
            setNotification({ type: 'success', message: 'Poster uploaded!' });
        } catch (error) { console.error("Upload failed:", error); setNotification({ type: 'error', message: 'Upload failed, using local preview.' }); }
    };

    const handleLogoUpload = async (e) => {
        if (!e.target.files[0]) return;
        const file = e.target.files[0];
        const localUrl = URL.createObjectURL(file);
        const newLogo = { url: localUrl, file, id: Date.now() };
        setUploadedImages(prev => ({ ...prev, logos: [...prev.logos, newLogo] }));
        try {
            const storageRef = ref(storage, `press_kits/${filmData.id}/logos/${Date.now()}_${file.name}`);
            const snap = await uploadBytes(storageRef, file);
            const url = await getDownloadURL(snap.ref);
            setUploadedImages(prev => ({ ...prev, logos: prev.logos.map(l => l.id === newLogo.id ? { ...l, url } : l) }));
        } catch (error) { console.error("Logo upload failed:", error); }
    };
    const removeLogo = (id) => { setUploadedImages(prev => ({ ...prev, logos: prev.logos.filter(l => l.id !== id) })); };

    const handleHeadshotUpload = async (e, creditId) => {
        if (!e.target.files[0]) return;
        const file = e.target.files[0];
        const localUrl = URL.createObjectURL(file);
        const creditIndex = pressKitData.full_credits.findIndex(c => c.id === creditId);
        if (creditIndex === -1) return;
        setUploadedImages(prev => ({ ...prev, headshots: { ...prev.headshots, [creditId]: { url: localUrl, file, id: Date.now() } } }));
        handleCreditChange(creditIndex, 'headshot_url', localUrl);
        try {
            const storageRef = ref(storage, `press_kits/${filmData.id}/headshots/${Date.now()}_${file.name}`);
            const snap = await uploadBytes(storageRef, file);
            const url = await getDownloadURL(snap.ref);
            setUploadedImages(prev => ({ ...prev, headshots: { ...prev.headshots, [creditId]: { url, id: Date.now() } } }));
            handleCreditChange(creditIndex, 'headshot_url', url);
        } catch (error) { console.error("Headshot upload failed:", error); }
    };

    const handleSave = async () => {
        if (!filmData?.id) return;
        setIsSaving(true);
        try {
            const dataToSave = {
                ...pressKitData,
                logos: uploadedImages.logos.map(l => ({ url: l.url, id: l.id })),
                gallery: uploadedImages.gallery.map(g => ({ url: g.url, id: g.id })),
                headshots: Object.entries(uploadedImages.headshots).reduce((acc, [creditId, data]) => {
                    acc[creditId] = { url: data.url, id: data.id };
                    return acc;
                }, {})
            };

            if (saveFilmData) {
                await saveFilmData({
                    ...filmData,
                    press_kit: dataToSave,
                    posterUrl: pressKitData.posterUrl,
                    logline: pressKitData.logline
                });
                setNotification({ type: 'success', message: 'Press kit saved successfully!' });
            } else {
                throw new Error("Save function not provided");
            }
        } catch (error) {
            console.error('Save failed:', error);
            setNotification({ type: 'error', message: 'Failed to save press kit' });
        } finally {
            setIsSaving(false);
        }
    };

    const derivedCast = (pressKitData.full_credits || []).filter(c => (c.type === 'cast' || c.department === 'Cast') && c.is_featured);
    const derivedCrew = (pressKitData.full_credits || []).filter(c => (c.type === 'crew' || c.department === 'Crew') && c.is_featured);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm">
            <div className="w-full h-full max-w-[98vw] max-h-[98vh] bg-zinc-900 rounded-lg shadow-2xl flex flex-col border border-zinc-800 relative">
                {/* Modal Header */}
                <div className="flex items-center justify-between p-4 border-b border-zinc-800 bg-zinc-950">
                    <div className="flex items-center gap-3">
                        <FileText size={20} className="text-purple-500" />
                        <h2 className="text-xl font-bold text-white">Press Kit Builder</h2>
                        <span className="text-sm text-zinc-500 hidden md:inline">— {filmData.title}</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <AppButton 
                            onClick={handleSave} 
                            disabled={isSaving}
                            size="sm"
                            icon={Save}
                            className="bg-purple-600 hover:bg-purple-500"
                        >
                            {isSaving ? 'Saving...' : 'Save Changes'}
                        </AppButton>
                        <button 
                            onClick={onClose}
                            className="p-2 hover:bg-zinc-800 rounded transition-all"
                        >
                            <X size={20} className="text-zinc-400" />
                        </button>
                    </div>
                </div>

                {/* Modal Content */}
                <div className="flex flex-1 overflow-hidden relative">
                    {/* Left Panel - Data Input */}
                    <div className="w-[400px] border-r border-zinc-800 overflow-y-auto p-6 space-y-6 bg-zinc-900/50">
                        
                        {/* CSV Import */}
                        <div className="bg-zinc-800 p-4 rounded-lg border border-zinc-700">
                            <h4 className="text-sm font-bold text-zinc-300 mb-2">Import Cast & Crew</h4>
                            <p className="text-xs text-zinc-500 mb-3">Upload .csv to auto-fill names, roles, and bios.</p>
                            <AppButton onClick={() => csvInputRef.current?.click()} size="sm" variant="secondary" className="w-full" icon={UploadCloud}>Import CSV</AppButton>
                            <input type="file" ref={csvInputRef} onChange={handleCsvImport} accept=".csv" className="hidden" />
                        </div>

                        {/* Poster & Logos */}
                        <div>
                            <button onClick={() => setOpenSections(prev => ({ ...prev, poster: !prev.poster }))} className="w-full flex items-center justify-between text-sm font-bold text-zinc-300 mb-2 uppercase tracking-wider">
                                <span>Key Art & Logos</span>
                                {openSections.poster ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                            </button>
                            
                            {openSections.poster && (
                                <div className="space-y-4 bg-zinc-800/30 p-4 rounded-lg">
                                    {/* Poster */}
                                    <div>
                                        <div className="flex justify-between items-center mb-2">
                                            <label className="text-xs text-zinc-500 block font-bold">OFFICIAL POSTER</label>
                                            {uploadedImages.posters?.[0]?.url && (
                                                <button 
                                                    onClick={() => setAdjustingImage(adjustingImage === 'poster' ? null : 'poster')}
                                                    className={`p-1 rounded flex items-center gap-1 text-[10px] font-bold uppercase ${adjustingImage === 'poster' ? 'bg-purple-600 text-white' : 'bg-zinc-700 text-zinc-300'}`}
                                                >
                                                    <Settings size={12} /> Adjust
                                                </button>
                                            )}
                                        </div>
                                        {uploadedImages.posters?.[0]?.url ? (
                                            <div className="group relative">
                                                <img src={uploadedImages.posters[0].url} alt="Poster" className="w-full h-64 object-contain bg-black rounded-lg border border-zinc-700"/>
                                                <label className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center cursor-pointer transition-all rounded-lg">
                                                    <span className="text-xs font-bold text-white flex items-center gap-2"><ImageIcon size={14}/> Change Poster</span>
                                                    <input type="file" hidden accept="image/*" onChange={handlePosterUpload}/>
                                                </label>
                                            </div>
                                        ) : (
                                            <label className="w-full h-32 border-2 border-dashed border-zinc-700 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-zinc-600 hover:bg-zinc-800/30 transition-all">
                                                <ImageIcon size={24} className="text-zinc-600 mb-2" />
                                                <span className="text-xs text-zinc-500">Upload Poster</span>
                                                <input type="file" hidden accept="image/*" onChange={handlePosterUpload}/>
                                            </label>
                                        )}
                                    </div>
                                    {/* Logos */}
                                    <div>
                                        <label className="text-xs text-zinc-500 mb-2 block font-bold">COMPANY LOGOS (Transparent PNG)</label>
                                        <div className="grid grid-cols-3 gap-2 mb-2">
                                            {uploadedImages.logos?.map(logo => (
                                                <div key={logo.id} className="relative group aspect-square bg-zinc-700/50 rounded border border-zinc-700 flex items-center justify-center p-2">
                                                    <img src={logo.url} alt="Logo" className="max-w-full max-h-full object-contain" />
                                                    <button onClick={() => removeLogo(logo.id)} className="absolute top-0 right-0 p-1 bg-red-500 text-white rounded-bl opacity-0 group-hover:opacity-100 transition-opacity"><X size={10} /></button>
                                                </div>
                                            ))}
                                            <label className="aspect-square border-2 border-dashed border-zinc-700 rounded flex flex-col items-center justify-center cursor-pointer hover:bg-zinc-800/50">
                                                <Plus size={16} className="text-zinc-500" />
                                                <span className="text-[10px] text-zinc-500 mt-1">Add</span>
                                                <input type="file" hidden accept="image/*" onChange={handleLogoUpload}/>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Film Details */}
                        <div>
                            <button onClick={() => setOpenSections(prev => ({ ...prev, details: !prev.details }))} className="w-full flex items-center justify-between text-sm font-bold text-zinc-300 mb-2 uppercase tracking-wider">
                                <span>Film Details</span>
                                {openSections.details ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                            </button>
                            
                            {openSections.details && (
                                <div className="space-y-3 bg-zinc-800/30 p-4 rounded-lg">
                                    <div><label className="text-xs text-zinc-500 uppercase font-bold">Logline</label><AppTextarea value={pressKitData.logline || ''} onChange={e => setPressKitData(prev => ({...prev, logline: e.target.value}))} rows={2} className="text-sm" placeholder="The hook..."/></div>
                                    <div><label className="text-xs text-zinc-500 uppercase font-bold">Synopsis</label><AppTextarea value={pressKitData.synopsis || ''} onChange={e => setPressKitData(prev => ({...prev, synopsis: e.target.value}))} rows={4} className="text-sm" placeholder="Full story summary..."/></div>
                                    <div><label className="text-xs text-zinc-500 uppercase font-bold">Director's Statement</label><AppTextarea value={pressKitData.director_statement || ''} onChange={e => setPressKitData(prev => ({...prev, director_statement: e.target.value}))} rows={4} className="text-sm" placeholder="Vision and inspiration..."/></div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <div><label className="text-xs text-zinc-500 uppercase font-bold">Runtime</label><AppInput value={pressKitData.runtime || ''} onChange={e => setPressKitData(prev => ({...prev, runtime: e.target.value}))} className="text-sm" placeholder="90 min"/></div>
                                        <div><label className="text-xs text-zinc-500 uppercase font-bold">Release</label><AppInput value={filmData.releaseWindow || ''} onChange={e => saveFilmData({...filmData, releaseWindow: e.target.value})} className="text-sm" placeholder="Fall 2025"/></div>
                                    </div>
                                    <div><label className="text-xs text-zinc-500 uppercase font-bold">Production Company</label><AppInput value={pressKitData.production_company || ''} onChange={e => setPressKitData(prev => ({...prev, production_company: e.target.value}))} className="text-sm" placeholder="Company name"/></div>
                                </div>
                            )}
                        </div>

                        {/* REVIEWS & LAURELS */}
                            <div>
                                <button onClick={() => setOpenSections(prev => ({ ...prev, reviews: !prev.reviews }))} className="w-full flex items-center justify-between text-sm font-bold text-zinc-300 mb-2 uppercase tracking-wider">
                                    <span>Reviews & Laurels</span>
                                    {openSections.reviews ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                </button>
                                
                                {openSections.reviews && (
                                    <div className="space-y-4 bg-zinc-800/30 p-4 rounded-lg">
                                        {/* Reviews */}
                                        <div>
                                            <div className="flex justify-between items-center mb-2">
                                                <label className="text-xs text-zinc-500 font-bold flex items-center gap-1"><Star size={10} /> PRESS QUOTES</label>
                                                <button onClick={handleAddReview} className="text-xs text-purple-400 font-bold flex items-center gap-1"><Plus size={10} /> ADD</button>
                                            </div>
                                            <div className="space-y-2">
                                                {pressKitData.reviews.map((review, idx) => (
                                                    <div key={review.id} className="bg-zinc-900 p-2 rounded border border-zinc-700">
                                                        <textarea 
                                                            value={review.quote} 
                                                            onChange={e => updateReview(idx, 'quote', e.target.value)}
                                                            placeholder="Quote..."
                                                            className="w-full bg-transparent text-sm text-white focus:outline-none mb-2 resize-none"
                                                            rows={2}
                                                        />
                                                        <div className="flex gap-2">
                                                            <input 
                                                                value={review.source} 
                                                                onChange={e => updateReview(idx, 'source', e.target.value)}
                                                                placeholder="Source (e.g. Variety)"
                                                                className="w-1/2 bg-zinc-800 text-xs px-2 py-1 rounded border-none"
                                                            />
                                                            <input 
                                                                value={review.author} 
                                                                onChange={e => updateReview(idx, 'author', e.target.value)}
                                                                placeholder="Author"
                                                                className="w-1/2 bg-zinc-800 text-xs px-2 py-1 rounded border-none"
                                                            />
                                                            <button onClick={() => removeReview(idx)} className="text-zinc-500 hover:text-red-500"><Trash2 size={12}/></button>
                                                        </div>
                                                    </div>
                                                ))}
                                                {pressKitData.reviews.length === 0 && <p className="text-xs text-zinc-600 italic text-center py-2">No reviews added</p>}
                                            </div>
                                        </div>
    
                                        {/* Laurels */}
                                        <div className="border-t border-zinc-700 pt-4">
                                            <div className="flex justify-between items-center mb-2">
                                                <label className="text-xs text-zinc-500 font-bold flex items-center gap-1"><Award size={10} /> LAURELS</label>
                                                <button onClick={handleAddLaurel} className="text-xs text-purple-400 font-bold flex items-center gap-1"><Plus size={10} /> ADD</button>
                                            </div>
                                            <div className="space-y-2">
                                                {pressKitData.laurels.map((laurel, idx) => (
                                                    <div key={laurel.id} className="bg-zinc-900 p-2 rounded border border-zinc-700 flex gap-2 items-center">
                                                        <div className="flex-1 space-y-1">
                                                            <input 
                                                                value={laurel.festival} 
                                                                onChange={e => updateLaurel(idx, 'festival', e.target.value)}
                                                                placeholder="Festival Name"
                                                                className="w-full bg-transparent text-sm font-bold focus:outline-none"
                                                            />
                                                            <div className="flex gap-2">
                                                                <input 
                                                                    value={laurel.award_name} 
                                                                    onChange={e => updateLaurel(idx, 'award_name', e.target.value)}
                                                                    placeholder="Award (e.g. Winner)"
                                                                    className="w-2/3 bg-zinc-800 text-[10px] px-1 rounded border-none text-zinc-400"
                                                                />
                                                                <input 
                                                                    value={laurel.year} 
                                                                    onChange={e => updateLaurel(idx, 'year', e.target.value)}
                                                                    placeholder="Year"
                                                                    className="w-1/3 bg-zinc-800 text-[10px] px-1 rounded border-none text-zinc-400"
                                                                />
                                                            </div>
                                                        </div>
                                                        <button onClick={() => removeLaurel(idx)} className="text-zinc-500 hover:text-red-500"><Trash2 size={12}/></button>
                                                    </div>
                                                ))}
                                                {pressKitData.laurels.length === 0 && <p className="text-xs text-zinc-600 italic text-center py-2">No laurels added</p>}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        {/* GALLERY */}
                        <div>
                             <button onClick={() => setOpenSections(prev => ({ ...prev, gallery: !prev.gallery }))} className="w-full flex items-center justify-between text-sm font-bold text-zinc-300 mb-2 uppercase tracking-wider">
                                <span>Photo Gallery</span>
                                {openSections.gallery ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                            </button>

                            {openSections.gallery && (
                                <div className="space-y-4 bg-zinc-800/30 p-4 rounded-lg">
                                    <div className="grid grid-cols-3 gap-2">
                                        {uploadedImages.gallery.map(img => (
                                            <div key={img.id} className="relative group aspect-video bg-zinc-900 rounded border border-zinc-700 overflow-hidden">
                                                <img src={img.url} className="w-full h-full object-cover" alt="Gallery" />
                                                <button onClick={() => removeGalleryImage(img.id)} className="absolute top-1 right-1 bg-red-500 p-1 rounded text-white opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={10} /></button>
                                            </div>
                                        ))}
                                        <label className="aspect-video border-2 border-dashed border-zinc-700 rounded flex flex-col items-center justify-center cursor-pointer hover:bg-zinc-800/50 hover:border-zinc-500 transition-all">
                                            <Camera size={20} className="text-zinc-500 mb-1" />
                                            <span className="text-[10px] text-zinc-500 font-bold uppercase">Add Photos</span>
                                            <input type="file" multiple accept="image/*" hidden onChange={handleGalleryUpload} />
                                        </label>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Credits List */}
                        <div>
                            <button onClick={() => setOpenSections(prev => ({ ...prev, credits: !prev.credits }))} className="w-full flex items-center justify-between text-sm font-bold text-zinc-300 mb-2 uppercase tracking-wider">
                                <span>Credits List</span>
                                {openSections.credits ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                            </button>
                            
                            {openSections.credits && (
                                <div>
                                    <div className="flex justify-between items-center mb-3">
                                        <span className="text-xs text-zinc-500">{pressKitData.full_credits?.length || 0} credits</span>
                                        <button onClick={handleAddCredit} className="text-xs text-purple-400 hover:text-purple-300 flex items-center gap-1 font-bold"> <Plus size={12} /> ADD NEW </button>
                                    </div>
                                    <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
                                        {(pressKitData.full_credits || []).map((credit, index) => (
                                            <div key={credit.id || index} className="bg-zinc-800 p-3 rounded border border-zinc-700 shadow-sm">
                                                <div className="flex items-start gap-2 mb-2">
                                                    <div className="flex-1 space-y-2">
                                                        <input className="w-full bg-transparent text-sm font-bold text-white focus:outline-none" value={credit.name} onChange={(e) => handleCreditChange(index, 'name', e.target.value)} placeholder="NAME" />
                                                        <input className="w-full bg-transparent text-xs text-zinc-400 focus:outline-none uppercase" value={credit.role} onChange={(e) => handleCreditChange(index, 'role', e.target.value)} placeholder="ROLE" />
                                                        <select className="w-full bg-zinc-900 text-xs text-zinc-400 border border-zinc-700 rounded px-2 py-1" value={credit.type || (credit.department === 'Cast' ? 'cast' : 'crew')} onChange={(e) => handleCreditChange(index, 'type', e.target.value)}>
                                                            <option value="cast">Cast</option>
                                                            <option value="crew">Crew</option>
                                                        </select>
                                                    </div>
                                                    <button onClick={() => handleDeleteCredit(index)} className="text-zinc-600 hover:text-red-500 p-1"> <Trash2 size={14} /> </button>
                                                </div>
                                                
                                                <div className="bg-zinc-900/50 p-2 rounded mt-2">
                                                    <label className="flex items-center justify-between cursor-pointer group mb-2">
                                                        <span className={`text-xs font-bold ${credit.is_featured ? 'text-purple-400' : 'text-zinc-500'}`}> {credit.is_featured ? '★ FEATURED' : 'Not Featured'} </span>
                                                        <input type="checkbox" checked={!!credit.is_featured} onChange={() => handleToggleFeature(index)} className="rounded accent-purple-500" />
                                                    </label>

                                                    {credit.is_featured && (
                                                        <div className="space-y-2 animate-in fade-in slide-in-from-top-2">
                                                            <textarea className="w-full bg-zinc-900 text-xs text-zinc-300 border border-zinc-700 rounded p-2" value={credit.bio || ''} onChange={(e) => handleCreditChange(index, 'bio', e.target.value)} placeholder="Enter bio for Bio Page..." rows={3} />
                                                            <div className="flex items-center justify-between">
                                                                <button onClick={() => handleGenerateBio(index)} disabled={isGeneratingBio} className="text-[10px] text-purple-400 hover:text-purple-300 flex items-center gap-1 disabled:opacity-50"> <WandSparkles size={10} /> AI Bio </button>
                                                                <div className="flex items-center gap-2">
                                                                    {(credit.headshot_url || uploadedImages.headshots?.[credit.id]?.url) && (
                                                                         <img src={credit.headshot_url || uploadedImages.headshots[credit.id]?.url} alt="Headshot" className="w-6 h-6 rounded-full object-cover border border-zinc-600" />
                                                                    )}
                                                                    
                                                                    {(credit.headshot_url || uploadedImages.headshots?.[credit.id]?.url) && (
                                                                        <button 
                                                                            onClick={() => setAdjustingImage(adjustingImage === credit.id ? null : credit.id)}
                                                                            className={`p-1 rounded ${adjustingImage === credit.id ? 'bg-purple-600 text-white' : 'bg-zinc-700 text-zinc-300'}`}
                                                                        >
                                                                            <Settings size={12} />
                                                                        </button>
                                                                    )}

                                                                    <label className="cursor-pointer text-[10px] bg-zinc-700 hover:bg-zinc-600 text-white px-2 py-1 rounded">
                                                                        {credit.headshot_url ? 'Change' : 'Add'}
                                                                        <input type="file" hidden accept="image/*" onChange={(e) => handleHeadshotUpload(e, credit.id)} />
                                                                    </label>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right Panel - Live Preview */}
                    <div className="flex-1 overflow-hidden bg-black flex flex-col">
                        <div className="flex-1 overflow-y-auto p-4">
                            <ProfessionalPressKitGenerator 
                                filmData={{
                                    ...filmData,
                                    cast: derivedCast,
                                    crew: derivedCrew,
                                    full_credits: pressKitData.full_credits || [],
                                    logline: pressKitData.logline,
                                    synopsis: pressKitData.synopsis,
                                    director_statement: pressKitData.director_statement,
                                    runtime: pressKitData.runtime,
                                    posterUrl: pressKitData.posterUrl,
                                    production_company: pressKitData.production_company,
                                    reviews: pressKitData.reviews, 
                                    laurels: pressKitData.laurels, 
                                    gallery: uploadedImages.gallery.length > 0 ? uploadedImages.gallery : pressKitData.gallery,
                                    title: filmData.title
                                }}
                                uploadedImages={uploadedImages} 
                                imageSettings={pressKitData.imageSettings} // Pass the settings to the renderer
                                setUploadedImages={setUploadedImages} 
                                setNotification={setNotification}
                                user={user}
                            />
                        </div>
                    </div>
                </div>

                {/* FLOATING IMAGE ADJUSTER - RENDERED AT ROOT LEVEL */}
                {adjustingImage && (
                    <ImageAdjuster 
                        settings={pressKitData.imageSettings[adjustingImage] || { x: 50, y: 50, scale: 1 }}
                        onUpdate={(s) => handleImageSettingsUpdate(adjustingImage, s)}
                        onClose={() => setAdjustingImage(null)}
                        imageName={adjustingImage === 'poster' ? 'Main Poster' : 'Headshot Adjust'}
                    />
                )}

            </div>
        </div>
    );
};

export default PressKitBuilderModal;